import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "@/lib/site";
import { Logo } from "@/components/site/Logo";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (!el) return;
    if (window.lenis) window.lenis.scrollTo(el, { offset: -70, duration: 1.2 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "backdrop-blur-xl bg-[#D9F4CD]/80 border-b border-[#17320B]/10" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto max-w-[1400px] px-5 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <button data-testid="logo" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="flex items-center group text-[#17320B] hover:text-[#7B9C62] transition-colors duration-300">
          <Logo className="text-2xl md:text-3xl" />
        </button>

        <div className="hidden md:flex items-center gap-9">
          {NAV_LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => go(l.href)}
              className="font-mono-eb text-xs uppercase tracking-[0.15em] text-[#17320B]/70 hover:text-[#17320B] transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
        </div>

        <button
          data-testid="nav-cta"
          onClick={() => go("#menu")}
          className="hidden md:inline-flex btn-sweep items-center rounded-full bg-[#17320B] px-6 py-2.5 font-mono-eb text-xs uppercase tracking-[0.15em] text-[#D9F4CD] font-semibold"
        >
          Order Now
        </button>

        <button data-testid="mobile-menu-toggle" className="md:hidden text-[#17320B]" onClick={() => setOpen(!open)}>
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-[#D9F4CD] border-b border-[#17320B]/10"
          >
            <div className="px-5 py-6 flex flex-col gap-5">
              {NAV_LINKS.map((l) => (
                <button key={l.href} onClick={() => go(l.href)} className="text-left font-display uppercase text-2xl text-[#17320B]">
                  {l.label}
                </button>
              ))}
              <button onClick={() => go("#menu")} className="mt-2 rounded-full bg-[#17320B] px-6 py-3 font-mono-eb text-xs uppercase tracking-[0.15em] text-[#D9F4CD] font-semibold">
                Order Now
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
